import React, { useState, useEffect, useCallback } from 'react';
import axiosInstance from 'config/axiosInstance';

import Tab from 'reusable/tab';
import Pagination from 'reusable/pagination';

import AllSubmissionsTable from './Table/AllSubmissionsTable';
import ExceedAlcoholLimitsTable from './Table/ExceedAlcoholLimitsTable';
import RecentSubmissionsTable from './Table/RecentSubmissionsTable';
import Toolbar from './Toolbar';
import { PAGE_SIZE } from 'config/constant';
import { useTranslation } from 'react-i18next';

import { EXCEED_FILTER_PARAM } from 'config/measurement';
import { businessDateRange, businessZoneQuery, toBusinessDate } from 'config/time';
import { useLatestRequest } from 'reusable/useLatestRequest';
import FetchFailed from 'reusable/FetchFailed';



const AlcoholManagement: React.FC = () => {
  const { t } = useTranslation('alcohol');
  const tabsData = [
    { label: `${t('tab.all')}`, value: 'allsubmission' },
    { label: `${t('tab.limit')}`, value: 'exceedalcohollimits' },
    { label: `${t('tab.recent')}`, value: 'recentsubmissions' },
  ];
  const today = toBusinessDate(new Date());
  const [activeTab, setActiveTab] = useState<string>(tabsData[0].value);
  const [searchValue, setSearchValue] = useState('');
  const [sortValue, setSortValue] = useState('');
  const [dateValue, setDateValue] = useState(today);
  const [currentPage, setCurrentPage] = useState(1);
  const [dataAlcoholTest, setDataAlcoholTest] = useState<any[]>([]);
  const [count, setCount] = useState(0);
  const [records, setRecords] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [dataForExport, setDataForExport] = useState<any[]>([]);
  // ADM-15。取得の失敗（空の一覧と区別する）と、最後の要求の応答だけを反映するための番号。
  const [fetchError, setFetchError] = useState<unknown>(null);
  const beginRequest = useLatestRequest();

  const handleTabClick = (value: string) => {
    setActiveTab(value);
    setCurrentPage(1);
  };

  const handleSortChange = (sortValue: string) => {
    setSortValue(sortValue);
    setCurrentPage(1);
  };

  const handleSearchChange = (value: string) => {
    setSearchValue(value);
    setCurrentPage(1);
  };

  const handleDateChange = (value: string) => {
    setDateValue(value);
    setCurrentPage(1);
  };

  const handlePageChange = (value: number) => {
    setCurrentPage(value);
  };

  const fetchProfilePicture = async (id: number) => {
    try {
      const response = await axiosInstance.get(`/v1/drivers/${id}/`);
      return response.data.profile_picture;
    } catch (error) {
      console.error(`Failed to fetch profile picture for driver ${id}`, error);
      return null;
    }
  };

  const fetchData = useCallback(async () => {
    const isCurrent = beginRequest();
    setLoading(true);
    try {
      let apiUrl = '/v1/alcohol_tests/';
      // 『作成日時』の並び替えは、alcohol_tests では testing_time（ordering_fields に create_time は無い）。
      // 最近の提出（drivers）は create_time をそのまま使う。
      const testSort = sortValue === 'create_time' ? 'testing_time' : sortValue;
      // ADM-05。業務日（日本時間）の範囲はtime.tsで作る。
      const { gte, lte } = businessDateRange(dateValue, dateValue);
      const startTime = gte ?? '';
      const endTime = lte ?? ''; 

      let fetchedData = [];

      if (activeTab === 'exceedalcohollimits') {
        apiUrl += `?${EXCEED_FILTER_PARAM}&order_by=${testSort}&page=${currentPage}&page_size=${PAGE_SIZE}&search=${searchValue}&testing_time__gte=${encodeURIComponent(startTime)}&testing_time__lte=${encodeURIComponent(endTime)}`;
      } else if (activeTab === 'recentsubmissions') {
        // RV-278・RV-279。画面の境界の計算と同じ時間帯を time_zone で渡す（範囲は下の gte・lte）。
        apiUrl = `/v1/drivers/recent_submission/?${businessZoneQuery()}alcoholtest__testing_time__gte=${encodeURIComponent(startTime)}&alcoholtest__testing_time__lte=${encodeURIComponent(endTime)}&order_by=${sortValue}&search=${searchValue}&page=${currentPage}`;
      } else {
        apiUrl += `?order_by=${testSort}&page=${currentPage}&page_size=${PAGE_SIZE}&search=${searchValue}&testing_time__gte=${encodeURIComponent(startTime)}&testing_time__lte=${encodeURIComponent(endTime)}`;
      }

      const response = await axiosInstance.get(apiUrl);
      fetchedData = response.data.results;

      if (activeTab === 'recentsubmissions') {
        // Fetch profile pictures for 'recentsubmissions'
        const updatedRecords = await Promise.all(
          fetchedData.map(async (record: any) => {
            const profilePicture = await fetchProfilePicture(record.id);
            return { ...record, profile_picture: profilePicture };
          })
        );
        if (!isCurrent()) return;
        setRecords(updatedRecords);
      } else {
        // Fetch profile pictures for other tabs
        const updatedDataAlcoholTest = await Promise.all(
          fetchedData.map(async (item: any) => {
            const profilePicture = await fetchProfilePicture(item.user.id);
            return { ...item, user: { ...item.user, profile_picture: profilePicture } };
          })
        );
        if (!isCurrent()) return;
        setDataAlcoholTest(updatedDataAlcoholTest);
      }

      setDataForExport(fetchedData);
      setCount(response.data.count);
      setFetchError(null);
    } catch (error) {
      if (!isCurrent()) return;
      console.error('Failed to fetch data:', error);
      setFetchError(error);
    } finally {
      if (isCurrent()) setLoading(false);
    }
  }, [searchValue, sortValue, dateValue, currentPage, activeTab, beginRequest]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // RV-166。一覧の条件。変わったら、表の「確認して再生」の古い応答を捨て、開いている動画を閉じる。
  const queryKey = `${activeTab}|${searchValue}|${sortValue}|${dateValue}|${currentPage}`;

  return (
    <div>
      {/* Tab Section */}
      <Tab tabs={tabsData} onTabClick={handleTabClick} />

      {/* Toolbar Section */}
      <div className="flex flex-row justify-between w-full">
        <Toolbar
          searchValue={searchValue}
          sortValue={sortValue}
          dateValue={dateValue}
          currentPage={currentPage}
          onSearchChange={handleSearchChange}
          onSortChange={handleSortChange}
          onDateChange={handleDateChange}
          onPageChange={handlePageChange}
          DataForExport={dataForExport}
          activetab={activeTab}
        />
      </div>

      {/* Loading Indicator */}
      {loading ? (
        <div
          className="flex justify-center items-center"
          style={{ height: '73vh' }}
        >
          <div>{t('ui.loading', { ns: 'common' })}</div>
        </div>
      ) : fetchError ? (
        <FetchFailed error={fetchError} onRetry={fetchData} />
      ) : (
        <div>
          {/* Table Section */}
          {activeTab === 'allsubmission' && (
            <AllSubmissionsTable submissions={dataAlcoholTest} queryKey={queryKey} />
          )}
          {activeTab === 'exceedalcohollimits' && (
            <ExceedAlcoholLimitsTable submissions={dataAlcoholTest} queryKey={queryKey} />
          )}
          {activeTab === 'recentsubmissions' && (
            <RecentSubmissionsTable records={records} />
          )}
        </div>
      )}

      {/* Pagination Section */}
      <Pagination
        allRows={count}
        rowsPerPage={PAGE_SIZE}
        currentPage={currentPage}
        onPageChange={setCurrentPage}
      />
    </div>
  );
};

export default AlcoholManagement;